import * as Y from "yjs";

/*
  Das Modell einer Präsentation.

  Eine Präsentation ist kein eigener Datensatz auf dem Server — sie ist ein
  `dokument` mit `art = "praesentation"`. Dieselbe Sammlung, dieselben
  Zugriffsregeln, dieselbe Echtzeit. Nur der Inhalt im Yjs-Dokument sieht
  anders aus:

      Y.Array "folien"
        └─ Y.Map { id, kaesten: Y.Array }
              └─ Y.Map { id, art: "text" | "bild",
                         x, y, breite, hoehe,   ← in Prozent der Folie
                         text: Y.Text,          ← nur bei Text
                         datei: "…" }           ← nur bei Bild

  ---------------------------------------------------------------
  Warum Prozent und nicht Pixel
  ---------------------------------------------------------------

  Dieselbe Folie erscheint in drei Größen: klein im Streifen der Vorschau,
  mittelgroß auf der Bühne, bildschirmfüllend im Vorführmodus — und auf
  Papier. Pixel würden bei jedem Wechsel verrutschen; ein Kasten, der auf
  dem Rechner sitzt, säße auf dem Beamer daneben.

  Mit Prozent ist die Folie ein Blatt ohne feste Größe, und alles darauf
  wächst mit. Auch die Schrift: Sie wird in Einheiten der Folienbreite
  gerechnet, nicht in Punkten.

  ---------------------------------------------------------------
  16:9
  ---------------------------------------------------------------

  Das Format jedes Beamers der letzten fünfzehn Jahre und jedes
  Konferenzraum-Fernsehers. 4:3 gibt es in Rathäusern noch, aber wer heute
  eine Präsentation anlegt, zeigt sie auf 16:9.
*/

export const SEITENVERHAELTNIS = 16 / 9;

/*
  Kennungen, die kein zweites Gerät zufällig auch vergibt.

  Zwei Fraktionsmitglieder legen offline dieselbe Folie an; hätten beide
  „folie-3", zeigten hinterher alle Kommentare auf dieselbe. Deshalb Zufall
  und keine laufende Nummer — dieselbe Überlegung wie bei den Absätzen des
  ersten Editors.
*/
function neueId(vorsatz) {
  const zufall = Math.random().toString(36).slice(2, 10);
  return `${vorsatz}-${Date.now().toString(36)}${zufall}`;
}

export function folienVon(doc) {
  return doc.getArray("folien");
}

/*
  Ein Textkasten.

  Die Maße sind Prozent der Folie: `x` und `y` die linke obere Ecke,
  `breite` und `hoehe` die Ausdehnung. `groesse` ist die Schrifthöhe,
  ebenfalls in Prozent der Folienhöhe — damit sie mitwächst.
*/
export function baueKasten({
  art = "text",
  x,
  y,
  breite,
  hoehe,
  groesse = 5,
  datei = "",
  sammlung = "",
  dateiname = "",
}) {
  const map = new Y.Map();
  map.set("id", neueId("kasten"));
  map.set("art", art);
  map.set("x", x);
  map.set("y", y);
  map.set("breite", breite);
  map.set("hoehe", hoehe);
  map.set("groesse", groesse);
  if (art === "text") {
    map.set("text", new Y.Text());
  } else {
    /*
      Ein Bild merkt sich alles, was seine Adresse braucht: die Kennung des
      Dateisatzes, die Sammlung und den Dateinamen.

      Warum nicht nur die Kennung und den Rest nachschlagen? Weil sonst
      jeder, der die Folie öffnet, erst eine zweite Abfrage bräuchte, bevor
      überhaupt ein Bild erscheint — und weil der Vorführmodus dann von
      einer Abfrage abhinge, die im Ratssaal auch scheitern kann. Drei
      kurze Zeichenketten mehr im Dokument sind der bessere Tausch.
    */
    map.set("datei", datei);
    map.set("sammlung", sammlung);
    map.set("dateiname", dateiname);
  }
  return map;
}

/* Aus den drei gemerkten Angaben wieder etwas machen, das eine Adresse ergibt. */
export function bildSatz(kasten) {
  const id = kasten.get("datei");
  const sammlung = kasten.get("sammlung");
  const dateiname = kasten.get("dateiname");
  if (!id || !sammlung || !dateiname) return null;
  return { id, collectionId: sammlung, datei: dateiname };
}

/*
  Eine neue Folie.

  Sie kommt nicht leer, sondern mit Titel und Inhaltskasten an den
  gewohnten Stellen. Eine weiße Fläche ohne Einstieg wäre der schlechtere
  Anfang — man soll tippen können, nicht erst einen Kasten aufziehen
  müssen. Verschieben lässt sich beides danach.
*/
export function baueFolie(reihenfolge = 0) {
  const folie = new Y.Map();
  folie.set("id", neueId("folie"));
  folie.set("reihenfolge", reihenfolge);

  const kaesten = new Y.Array();
  kaesten.push([
    baueKasten({ art: "text", x: 8, y: 9, breite: 84, hoehe: 16, groesse: 8 }),
    baueKasten({ art: "text", x: 8, y: 30, breite: 84, hoehe: 58, groesse: 4.5 }),
  ]);
  folie.set("kaesten", kaesten);

  return folie;
}

export function stelleErsteFolieSicher(doc) {
  const folien = folienVon(doc);
  if (folien.length > 0) return;
  doc.transact(() => folien.push([baueFolie(0)]));
}

/*
  Die Folien in ihrer Reihenfolge — nach der ZAHL, nicht nach der Stelle
  im Yjs-Feld.

  Der Grund steht bei `verschiebeFolie`: Umsortieren im Feld selbst würde
  die Textobjekte neu aufbauen und damit jeden Kommentaranker dieser Folie
  zerreißen.
*/
export function sortierteFolien(doc) {
  return folienVon(doc)
    .toArray()
    .map((folie, stelle) => ({ folie, stelle }))
    .sort((a, b) => (a.folie.get("reihenfolge") ?? a.stelle) - (b.folie.get("reihenfolge") ?? b.stelle));
}

/* Für den Streifen und den Vorführmodus: die Folien als schlichte Werte. */
export function alsFolienliste(doc) {
  return sortierteFolien(doc)
    .map(({ folie }) => ({
      id: folie.get("id"),
      kaesten: (folie.get("kaesten") || new Y.Array())
        .toArray()
        .map((k) => ({
          id: k.get("id"),
          art: k.get("art"),
          x: k.get("x"),
          y: k.get("y"),
          breite: k.get("breite"),
          hoehe: k.get("hoehe"),
          groesse: k.get("groesse"),
          datei: k.get("datei") || "",
          sammlung: k.get("sammlung") || "",
          dateiname: k.get("dateiname") || "",
          text: k.get("text") ? k.get("text").toString() : "",
        })),
    }));
}

/*
  Eine Folie an eine andere Stelle schieben.

  ---------------------------------------------------------------
  Warum über eine Zahl und nicht über das Feld
  ---------------------------------------------------------------

  Yjs kennt kein „verschieben": Ein Typ, der schon in einem Dokument
  steckt, lässt sich nicht ein zweites Mal einfügen. Man müsste ihn neu
  aufbauen — und damit wären die Textobjekte andere. Jeder Kommentar an
  dieser Folie zeigte danach ins Leere, weil seine Verankerung auf den
  alten Text zeigt.

  Deshalb bleibt die Folie, wo sie ist, und bekommt nur eine neue Zahl.
  Gerechnet wird die Mitte zwischen den beiden Nachbarn — so muss keine
  andere Folie angefasst werden, und zwei Leute, die gleichzeitig
  umsortieren, treten sich nicht auf die Füße.
*/
export function verschiebeFolie(doc, folieId, nachStelle) {
  const sortiert = sortierteFolien(doc);
  const jetzt = sortiert.findIndex(({ folie }) => folie.get("id") === folieId);
  if (jetzt === -1 || jetzt === nachStelle) return;

  const ohne = sortiert.filter((_, i) => i !== jetzt);
  const ziel = Math.max(0, Math.min(nachStelle, ohne.length));

  const zahlVon = (eintrag) => eintrag.folie.get("reihenfolge") ?? 0;
  const davor = ziel > 0 ? zahlVon(ohne[ziel - 1]) : null;
  const danach = ziel < ohne.length ? zahlVon(ohne[ziel]) : null;

  let neu;
  if (davor === null && danach === null) neu = 0;
  else if (davor === null) neu = danach - 1;
  else if (danach === null) neu = davor + 1;
  else neu = (davor + danach) / 2;

  doc.transact(() => sortiert[jetzt].folie.set("reihenfolge", neu));
}

/* Die nächste freie Zahl — für eine Folie, die hinten angehängt wird. */
export function naechsteReihenfolge(doc) {
  const zahlen = folienVon(doc)
    .toArray()
    .map((f) => f.get("reihenfolge") ?? 0);
  return zahlen.length === 0 ? 0 : Math.max(...zahlen) + 1;
}
